// Hooks pour les comptes copropriétaires
// Utilise la vue owner_balances_view (soldes calculés depuis les écritures comptables)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { OwnerBalance, OwnerPayment, OwnerPaymentInsert, PaymentDistributionInsert } from '../types'
import type { PaymentFormData } from '../components/PaymentModal'
import { getTransitAccount, getPaymentJournal } from '../components/PaymentModal'
import type { LettrageLineAmount } from './useLettrage'

const QUERY_KEY = 'owner_balances'

// Récupère les soldes de tous les copropriétaires d'une résidence
export function useOwnerAccounts(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, residenceId],
    queryFn: async (): Promise<OwnerBalance[]> => {
      const { data, error } = await supabase
        .from('owner_balances_view')
        .select('*')
        .eq('residence_id', residenceId!)
        .order('last_name')

      if (error) throw error
      return data as OwnerBalance[]
    },
    enabled: !!residenceId,
  })
}

// Récupère le solde d'un copropriétaire spécifique
export function useOwnerAccount(ownerId?: string, residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', ownerId, residenceId],
    queryFn: async (): Promise<OwnerBalance | null> => {
      const { data, error } = await supabase
        .from('owner_balances_view')
        .select('*')
        .eq('owner_id', ownerId!)
        .eq('residence_id', residenceId!)
        .maybeSingle()

      if (error) throw error
      return data as OwnerBalance | null
    },
    enabled: !!ownerId && !!residenceId,
  })
}

// Récupère les soldes des copropriétaires en impayé
export function useUnpaidOwners(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'unpaid', residenceId],
    queryFn: async (): Promise<OwnerBalance[]> => {
      const { data, error } = await supabase
        .from('unpaid_owners_view')
        .select('*')
        .eq('residence_id', residenceId!)
        .order('balance', { ascending: false })

      if (error) throw error
      return data as OwnerBalance[]
    },
    enabled: !!residenceId,
  })
}

// Statistiques des comptes copropriétaires
export function useOwnerAccountStats(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', residenceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('owner_balances_view')
        .select('balance, unpaid_amount')
        .eq('residence_id', residenceId!)

      if (error) throw error

      const balances = data || []
      return {
        totalAccounts: balances.length,
        totalBalance: balances.reduce((sum, b) => sum + (b.balance || 0), 0),
        unpaidCount: balances.filter(b => (b.balance || 0) > 0).length,
        unpaidAmount: balances.reduce((sum, b) => sum + Math.max(0, b.balance || 0), 0),
      }
    },
    enabled: !!residenceId,
  })
}

// ===== PAIEMENTS =====

// Récupère les paiements d'une résidence
export function useOwnerPayments(residenceId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: ['owner_payments', residenceId, fiscalYearId],
    queryFn: async (): Promise<OwnerPayment[]> => {
      let query = supabase
        .from('owner_payments')
        .select(`
          *,
          payer_owner:owners!owner_payments_payer_owner_id_fkey (
            id, first_name, last_name, email
          ),
          fiscal_year:fiscal_years!owner_payments_fiscal_year_id_fkey (
            id, name
          )
        `)
        .eq('residence_id', residenceId!)

      if (fiscalYearId) {
        query = query.eq('fiscal_year_id', fiscalYearId)
      }

      const { data, error } = await query.order('payment_date', { ascending: false })

      if (error) throw error
      return data as OwnerPayment[]
    },
    enabled: !!residenceId,
  })
}

// Créer un paiement
export function useCreateOwnerPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payment: OwnerPaymentInsert) => {
      const { data, error } = await supabase
        .from('owner_payments')
        .insert(payment)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['owner_payments', data.residence_id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, data.residence_id] })
    },
  })
}

// Créer les distributions d'un paiement
export function useCreatePaymentDistributions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ownerPaymentId,
      distributions,
    }: {
      ownerPaymentId: string
      distributions: Omit<PaymentDistributionInsert, 'owner_payment_id'>[]
    }) => {
      const items = distributions.map(d => ({
        ...d,
        owner_payment_id: ownerPaymentId,
      }))

      const { data, error } = await supabase
        .from('payment_distributions')
        .insert(items)
        .select()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner_payments'] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Valider un paiement
export function useValidateOwnerPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const { data, error } = await supabase
        .from('owner_payments')
        .update({
          status: 'validated',
          validated_at: new Date().toISOString(),
        })
        .eq('id', paymentId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['owner_payments', data.residence_id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, data.residence_id] })
    },
  })
}

// ===== ÉCRITURES D'UN COMPTE COPROPRIÉTAIRE =====

export interface OwnerAccountLine {
  id: string
  transaction_id: string
  transaction_date: string
  piece_number: string | null
  description: string
  debit: number
  credit: number
  balance: number // Solde cumulé
  label: string | null
  // Nouveau système de lettrage par montant
  lettered_amount: number // Montant déjà lettré
  remaining_amount: number // Montant restant à lettrer
  lettrage_status: 'unlettered' | 'partially_lettered' | 'fully_lettered'
  lettrage_codes: string[] // Codes des lettrages (peut être multiple)
  status: string
}

// Récupère les écritures comptables d'un compte propriétaire (450xxx) par lot
// Utilise la vue transaction_lines_with_lettrage pour les montants lettrés
export function useOwnerAccountLines(unitId?: string, residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'lines', unitId, residenceId],
    queryFn: async (): Promise<OwnerAccountLine[]> => {
      // Récupérer les lignes d'écritures du compte 450 pour ce lot
      // avec les informations de lettrage agrégées
      const { data, error } = await supabase
        .from('transaction_lines_with_lettrage')
        .select(`
          id,
          transaction_id,
          debit,
          credit,
          label,
          lettered_amount,
          remaining_amount,
          lettrage_status,
          transaction:transactions!inner(
            id,
            transaction_date,
            piece_number,
            description,
            status,
            residence_id
          ),
          residence_chart_account:residence_chart_accounts!inner(
            chart_account:chart_accounts!inner(code)
          )
        `)
        .eq('unit_id', unitId!)
        .eq('transaction.residence_id', residenceId!)
        .like('residence_chart_account.chart_account.code', '450%')

      if (error) throw error

      // Récupérer les codes de lettrage pour chaque ligne
      const lineIds = (data || []).map(l => l.id).filter((id): id is string => id !== null)
      const lettrageCodes: Record<string, string[]> = {}

      if (lineIds.length > 0) {
        const { data: lettrageData } = await supabase
          .from('lettrage_lines')
          .select(`
            transaction_line_id,
            lettrage:lettrages(display_code)
          `)
          .in('transaction_line_id', lineIds)

        // Grouper les codes par ligne
        for (const ll of lettrageData || []) {
          const code = (ll as any).lettrage?.display_code
          if (code) {
            if (!lettrageCodes[ll.transaction_line_id]) {
              lettrageCodes[ll.transaction_line_id] = []
            }
            if (!lettrageCodes[ll.transaction_line_id]!.includes(code)) {
              lettrageCodes[ll.transaction_line_id]!.push(code)
            }
          }
        }
      }

      // Transformer et calculer le solde cumulé
      let runningBalance = 0
      const lines: OwnerAccountLine[] = []

      // Trier par date puis par id pour un ordre stable
      const sortedData = [...(data || [])].sort((a, b) => {
        const dateA = (a as any).transaction?.transaction_date || ''
        const dateB = (b as any).transaction?.transaction_date || ''
        if (dateA !== dateB) return dateA.localeCompare(dateB)
        return (a.id || '').localeCompare(b.id || '')
      })

      for (const line of sortedData) {
        const tx = (line as any).transaction
        if (!tx || !line.id || !line.transaction_id) continue

        const debit = line.debit || 0
        const credit = line.credit || 0
        runningBalance += debit - credit

        lines.push({
          id: line.id,
          transaction_id: line.transaction_id,
          transaction_date: tx.transaction_date,
          piece_number: tx.piece_number,
          description: tx.description,
          debit,
          credit,
          balance: runningBalance,
          label: line.label,
          lettered_amount: (line as any).lettered_amount || 0,
          remaining_amount: (line as any).remaining_amount || Math.max(debit, credit),
          lettrage_status: (line as any).lettrage_status || 'unlettered',
          lettrage_codes: lettrageCodes[line.id] || [],
          status: tx.status,
        })
      }

      return lines
    },
    enabled: !!unitId && !!residenceId,
  })
}

// Interface pour les lignes avec montant restant à lettrer
export interface UnletteredLine {
  id: string
  transaction_id: string
  transaction_date: string | null
  description: string | null
  piece_number: string | null
  debit: number // Montant total de la ligne
  remaining_amount: number // Montant restant à lettrer
  lettered_amount: number // Montant déjà lettré
  lettrage_status: 'unlettered' | 'partially_lettered' | 'fully_lettered'
  label: string | null
  owner_id: string | null
}

// Récupère les écritures avec un montant restant à lettrer (pour affectation règlement)
// Inclut les lignes partiellement lettrées (remaining_amount > 0)
export function useUnletteredLines(unitId?: string, residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'unlettered', unitId, residenceId],
    queryFn: async (): Promise<UnletteredLine[]> => {
      // Utiliser la vue avec les montants lettrés
      const { data, error } = await supabase
        .from('transaction_lines_with_lettrage')
        .select(`
          id,
          transaction_id,
          debit,
          credit,
          label,
          owner_id,
          lettered_amount,
          remaining_amount,
          lettrage_status,
          transaction:transactions!inner(
            id,
            transaction_date,
            piece_number,
            description,
            status,
            residence_id
          ),
          residence_chart_account:residence_chart_accounts!inner(
            chart_account:chart_accounts!inner(code)
          )
        `)
        .eq('unit_id', unitId!)
        .eq('transaction.residence_id', residenceId!)
        .eq('transaction.status', 'validated')
        .like('residence_chart_account.chart_account.code', '450%')
        // Filtrer par remaining_amount > 0 n'est pas possible sur une vue calculée
        // On filtre côté JS

      if (error) throw error

      // Ne retourner que les débits avec un montant restant > 0
      // (appels de fonds pas entièrement réglés)
      return (data || [])
        .filter((line) => {
          if (!line.id || !line.transaction_id) return false
          const debit = line.debit || 0
          const remaining = (line as any).remaining_amount ?? debit
          return debit > 0 && remaining > 0.01
        })
        .map((line) => {
          const tx = (line as any).transaction
          const debit = line.debit || 0
          return {
            id: line.id!,
            transaction_id: line.transaction_id!,
            transaction_date: tx?.transaction_date || null,
            description: tx?.description || null,
            piece_number: tx?.piece_number || null,
            debit,
            remaining_amount: (line as any).remaining_amount ?? debit,
            lettered_amount: (line as any).lettered_amount ?? 0,
            lettrage_status: (line as any).lettrage_status ?? 'unlettered',
            label: line.label,
            owner_id: line.owner_id,
          }
        })
        .sort((a, b) => {
          const dateA = a.transaction_date || ''
          const dateB = b.transaction_date || ''
          return dateA.localeCompare(dateB)
        })
    },
    enabled: !!unitId && !!residenceId,
  })
}

// ===== CRÉATION DE RÈGLEMENT =====

interface CreatePaymentParams {
  residenceId: string
  fiscalYearId: string
  formData: PaymentFormData
  bankAccount512Id: string // ID du residence_chart_account 512 lié au compte bancaire
  lettrageAmounts?: LettrageLineAmount[] // Montants à lettrer par ligne (nouveau système)
}

/**
 * Hook pour créer un règlement copropriétaire
 * Génère l'écriture comptable selon le mode de paiement :
 * - Direct en 512 : Débit 512, Crédit 450
 * - Avec transit : Débit transitAccount (5112/517/53), Crédit 450
 */
export function useCreatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ residenceId, fiscalYearId, formData, bankAccount512Id, lettrageAmounts }: CreatePaymentParams) => {
      const transitAccount = getTransitAccount(formData.paymentMode)
      const journalCode = getPaymentJournal(formData.paymentMode)

      // Construire le libellé
      let description = 'Règlement'
      if (formData.payerName) {
        description += ` ${formData.payerName}`
      }
      if (formData.reference) {
        description += ` — ${formData.reference}`
      }

      // Récupérer l'ID du journal
      const { data: journal, error: journalError } = await supabase
        .from('journals')
        .select('id')
        .eq('code', journalCode)
        .single()

      if (journalError) throw new Error(`Journal ${journalCode} non trouvé`)

      // Déterminer le compte de débit
      let debitAccountId: string

      if (transitAccount) {
        // Compte transitoire (5112, 517, 53)
        const { data: transitAccounts, error: transitError } = await supabase
          .from('residence_chart_accounts')
          .select('id, chart_account:chart_accounts!inner(code)')
          .eq('residence_id', residenceId)
          .like('chart_account.code', `${transitAccount}%`)
          .limit(1)

        if (transitError) throw transitError

        if (!transitAccounts || transitAccounts.length === 0) {
          throw new Error(`Compte ${transitAccount} non trouvé pour cette résidence. Veuillez l'activer dans le plan comptable.`)
        }

        debitAccountId = transitAccounts[0]!.id
      } else {
        // Direct en 512 (banque sélectionnée)
        debitAccountId = bankAccount512Id
      }

      // Récupérer le compte 450 du lot (via unit_id du formData)
      const { data: account450, error: account450Error } = await supabase
        .from('residence_chart_accounts')
        .select('id, chart_account:chart_accounts!inner(code)')
        .eq('unit_id', formData.unitId)
        .single()

      if (account450Error) throw account450Error
      if (!account450) {
        throw new Error('Compte 450 non trouvé pour ce lot')
      }

      const creditAccountId = account450.id

      // Créer la transaction
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .insert({
          journal_id: journal.id,
          residence_id: residenceId,
          fiscal_year_id: fiscalYearId,
          transaction_date: formData.paymentDate,
          description,
          reference: formData.reference || null,
          status: 'draft',
        })
        .select()
        .single()

      if (txError) throw txError

      // Créer les lignes d'écriture
      const lines = [
        {
          transaction_id: transaction.id,
          residence_chart_account_id: debitAccountId,
          debit: formData.amount,
          credit: 0,
          label: description,
          owner_id: formData.payerId,
          unit_id: formData.unitId,
        },
        {
          transaction_id: transaction.id,
          residence_chart_account_id: creditAccountId,
          debit: 0,
          credit: formData.amount,
          label: description,
          owner_id: formData.payerId,
          unit_id: formData.unitId,
        },
      ]

      const { data: insertedLines, error: linesError } = await supabase
        .from('transaction_lines')
        .insert(lines)
        .select('id, credit')

      if (linesError) throw linesError

      // Valider la transaction immédiatement
      const { error: validateError } = await supabase.rpc('validate_transaction', {
        p_transaction_id: transaction.id,
      })

      if (validateError) throw validateError

      // ===== NOUVEAU SYSTÈME DE LETTRAGE PAR MONTANT =====
      // Si des montants de lettrage sont fournis, créer le lettrage
      if (lettrageAmounts && lettrageAmounts.length > 0) {
        // Récupérer la ligne crédit (450) qu'on vient de créer
        const newCreditLine = insertedLines?.find(l => (l.credit || 0) > 0)

        if (newCreditLine) {
          // Calculer le total des montants à lettrer (côté débit)
          const debitTotal = lettrageAmounts.reduce((sum, la) => sum + la.amount, 0)

          // Construire le payload JSONB pour create_lettrage
          // Inclut les lignes débit (appels de fonds) + la ligne crédit (règlement)
          const lineAmounts = [
            // Les débits avec leurs montants partiels
            ...lettrageAmounts.map(la => ({
              line_id: la.lineId,
              amount: la.amount,
            })),
            // Le crédit du règlement pour le total
            {
              line_id: newCreditLine.id,
              amount: debitTotal, // Le crédit lettré = somme des débits lettrés
            },
          ]

          try {
            await supabase.rpc('create_lettrage', {
              p_account_id: creditAccountId,
              p_line_amounts: lineAmounts,
            })
          } catch (lettrageError) {
            // Le lettrage peut échouer si déséquilibré ou autres raisons
            // On ne bloque pas la création du règlement pour autant
            console.warn('Lettrage automatique échoué:', lettrageError)
          }
        }
      }

      return {
        transactionId: transaction.id,
        hasTransitAccount: !!transitAccount,
        transitAccount,
      }
    },
    onSuccess: () => {
      // Invalider les caches - utiliser juste [QUERY_KEY] pour invalider toutes les sous-queries
      // (owner_balances, owner_balances/lines, owner_balances/unlettered, etc.)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['lettrage'] })
    },
  })
}
